import { skipToken } from "@reduxjs/toolkit/query";
import { useGetDisputesQuery } from "../api/answers";
import { usePartnerQuestionnaireState } from "./usePartnerQuestionnaireState";
import useGetCurrentUserDocument from "./useGetCurrentUserDocument";
import { useGetIncomesByUserIdQuery } from "../api/financials/incomes";
import { hasAllRequiredIncomeYears } from "../utils/incomeValidation";

interface UseNegotiationStateProps {
  isQuestionnaireComplete?: boolean;
  hasFinancials?: boolean;
  areAccountsLinked?: boolean;
}

interface UseNegotiationState {
  isLoading: boolean;
  isError: boolean;
  hasDisputes: boolean;
  isComplete: boolean;
  isDisabled: boolean;
  partnerHasFinancials: boolean;
  isPartnerQuestionnaireComplete: boolean;
}

export const useNegotiationState = (
  props: UseNegotiationStateProps = {},
): UseNegotiationState => {
  const {
    isQuestionnaireComplete = false,
    hasFinancials = false,
    areAccountsLinked = false,
  } = props;

  const {
    user,
    document,
    isLoading: isUserLoading,
    error: userError,
  } = useGetCurrentUserDocument();

  const {
    data: disputes,
    isLoading: isDisputesLoading,
    isError: isDisputesError,
  } = useGetDisputesQuery(user ? { userId: user.id } : skipToken, {
    refetchOnMountOrArgChange: true,
  });

  const {
    isPartnerQuestionnaireComplete,
    isLoading: isPartnerQuestionnaireLoading,
    isError: isPartnerQuestionnaireError,
  } = usePartnerQuestionnaireState();

  const partnerId =
    user?.id === document?.userAId ? document?.userBId : document?.userAId;

  const {
    data: partnerIncomes,
    isLoading: isPartnerIncomesLoading,
    isError: isPartnerIncomesError,
  } = useGetIncomesByUserIdQuery(
    partnerId && document
      ? { userId: partnerId, docId: document.id }
      : skipToken,
  );
  const partnerIncomeData = partnerIncomes ?? [];
  const partnerHasFinancials = hasAllRequiredIncomeYears(
    partnerIncomeData,
    document,
  );

  const isLoading =
    isUserLoading ||
    isDisputesLoading ||
    isPartnerQuestionnaireLoading ||
    isPartnerIncomesLoading;
  const isError =
    !!userError ||
    !!isDisputesError ||
    !!isPartnerQuestionnaireError ||
    !!isPartnerIncomesError;
  const hasDisputes = !!disputes && disputes.length > 0;

  const arePrerequisitesMet =
    areAccountsLinked &&
    isQuestionnaireComplete &&
    hasFinancials &&
    isPartnerQuestionnaireComplete &&
    partnerHasFinancials;

  const isDataReady = !isLoading && !isError && disputes !== undefined;

  const isDisabled = !isDataReady || !arePrerequisitesMet;
  const isComplete = isDataReady && arePrerequisitesMet && !hasDisputes;

  return {
    isLoading,
    isError,
    hasDisputes,
    isComplete,
    isDisabled,
    partnerHasFinancials,
    isPartnerQuestionnaireComplete,
  };
};
